// create-nfa-card.tsx

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Editor } from 'primereact/editor';
import 'quill/dist/quill.snow.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { ToastContext } from 'app/context/ToastContext';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { Checkbox } from 'primereact/checkbox';
import { createCard, getCard, updateCard } from 'app/api/nfaCardApi';
import AgentWidget from 'app/component/agent-widget';
import ClientWidget from 'app/component/client-widget';
import DateTextInput from 'app/component/date-text-input';

const CreateNfaCard: React.FC = () => {
  const { id } = useParams<{ id?: string }>();

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const { showSuccess, showError } = useContext(ToastContext);
  const [text, setText] = useState<string>('');
  const editorRef = useRef<Editor>(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const isAdminOrAssistant = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.ASSISTANT]),
  );

  useEffect(() => {
    if (id) {
      getCard(id)
        .then(response => {
          setData(response.data);
        })
        .catch(e => {
          console.error(e);
          showError('Chyba', e?.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;
    if (id) {
      call = updateCard(id, values);
    } else {
      call = createCard(values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'NFA Karta úspešne uložená.');
        setData(response.data);
        navigate(`/nfa-cards`);
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="card">
      <h3>{id ? 'Upraviť NFA Kartu' : 'Vytvoriť NFA Kartu'}</h3>
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}
      {!loading && (
        <div>
          <Formik
            initialValues={
              data
                ? data
                : {
                    title: '',
                    data: '',
                  }
            }
            // validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <div className="grid">
                    <div className="col-12">
                      <div className="p-field">
                        <label htmlFor="agent">Poradca</label>
                        <AgentWidget
                          id="agent"
                          value={values.agent?.id}
                          readonly={!isAdminOrAssistant}
                          onPersonChanged={e => {
                            void setFieldValue('agent.id', e);
                          }}
                          showCareer={true}
                          selectedCareerType={values.careerForConcludedBy}
                          onCareerChanged={e => {
                            void setFieldValue('careerForConcludedBy', e);
                          }}
                        />
                        <ErrorMessage name="agent" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12">
                      <label htmlFor="clients">Zoznam klientov</label>
                      <FieldArray name="clients">
                        {({ insert, remove, push }) => (
                          <div className="flex grid">
                            {values.clients?.length > 0 &&
                              values.clients.map((person, index) => {
                                return (
                                  <div key={person?.id} className="flex p-mb-2 col-12 md:col-6" style={{ marginBottom: '5px' }}>
                                    <ClientWidget
                                      id={`client.${index}.id`}
                                      key={person?.id}
                                      readonly={!isAdminOrAssistant}
                                      value={person?.id}
                                      onPersonChanged={e => {
                                        void setFieldValue(`clients.${index}.id`, e);
                                      }}
                                      onDelete={() => {
                                        remove(index);
                                      }}
                                    />
                                  </div>
                                );
                              })}

                            <div className="col-12 md:col-6" style={{ alignSelf: 'center', marginBottom: '20px' }}>
                              <div className="p-field">
                                {isAdminOrAssistant && (
                                  <Button
                                    style={{ backgroundColor: 'green' }}
                                    type="button"
                                    disabled={values.clients?.find(ic => !ic.id)}
                                    label="Pridať klienta"
                                    onClick={() => push({ id: null })}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </FieldArray>
                      <div />
                    </div>

                    <div className="col-12 md:col-4">
                      <div className="p-field">
                        <label htmlFor="prevodZmluv" className="text-900 font-semibold">
                          Prevod zmlúv
                        </label>
                        <Checkbox
                          disabled={!isAdminOrAssistant}
                          name="prevodZmluv"
                          onChange={e => void setFieldValue('prevodZmluv', e.checked)}
                          checked={values?.prevodZmluv}
                        ></Checkbox>
                      </div>
                    </div>

                    <div className="col-12 md:col-4">
                      <div className="p-field">
                        <label htmlFor="zaujemDruzstvo" className="text-900 font-semibold">
                          Záujem Družstvo NFA Invest
                        </label>
                        <Checkbox
                          name="zaujemDruzstvo"
                          disabled={!isAdminOrAssistant}
                          onChange={e => void setFieldValue('zaujemDruzstvo', e.checked)}
                          checked={values?.zaujemDruzstvo}
                        ></Checkbox>
                      </div>
                    </div>

                    <div className="col-12 md:col-4">
                      <div className="p-field">
                        <label htmlFor="zaujemInvestments" className="text-900 font-semibold">
                          Záujem NFA Investments
                        </label>
                        <Checkbox
                          name="zaujemInvestments"
                          disabled={!isAdminOrAssistant}
                          onChange={e => void setFieldValue('zaujemInvestments', e.checked)}
                          checked={values?.zaujemInvestments}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className="col-12 md:col-4">
                      <div className="p-field">
                        <label htmlFor="zaujemLegal" className="text-900 font-semibold">
                          Záujem NFA Legal Counseling
                        </label>
                        <Checkbox
                          name="zaujemLegal"
                          disabled={!isAdminOrAssistant}
                          onChange={e => void setFieldValue('zaujemLegal', e.checked)}
                          checked={values?.zaujemLegal}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className="col-12 md:col-4">
                      <div className="p-field">
                        <label htmlFor="zaujemAccounting" className="text-900 font-semibold">
                          Záujem NFA Accounting Services
                        </label>
                        <Checkbox
                          name="zaujemAccounting"
                          disabled={!isAdminOrAssistant}
                          onChange={e => void setFieldValue('zaujemAccounting', e.checked)}
                          checked={values?.zaujemAccounting}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className="col-12 md:col-4">
                      <div className="p-field">
                        <label htmlFor="prevodZmluv" className="text-900 font-semibold">
                          Záujem NFA Real Estate
                        </label>
                        <Checkbox
                          name="zaujemRealEstate"
                          disabled={!isAdminOrAssistant}
                          onChange={e => void setFieldValue('zaujemRealEstate', e.checked)}
                          checked={values?.zaujemRealEstate}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-field">
                        <label htmlFor="zaujemSpolupraca" className="text-900 font-semibold">
                          Záujem spolupráca
                        </label>
                        <Checkbox
                          name="zaujemSpolupraca"
                          disabled={!isAdminOrAssistant}
                          onChange={e => void setFieldValue('zaujemSpolupraca', e.checked)}
                          checked={values?.zaujemSpolupraca}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className="col-12 md:col-6 field">
                      <label htmlFor="prveStretnutie" className="text-900 font-semibold">
                        Dátum prvého stretnutia
                      </label>
                      <DateTextInput
                        id="prveStretnutie"
                        readOnly={!isAdminOrAssistant}
                        value={values.prveStretnutie}
                        onChange={val => void setFieldValue('prveStretnutie', val)}
                      />
                    </div>

                    <div className="col-12 md:col-6 field">
                      <label htmlFor="druheStretnutie" className="text-900 font-semibold">
                        Dátum druhého stretnutia
                      </label>
                      <DateTextInput
                        id="druheStretnutie"
                        readOnly={!isAdminOrAssistant}
                        value={values.druheStretnutie}
                        onChange={val => void setFieldValue('druheStretnutie', val)}
                      />
                    </div>

                    <div className="col-12 field">
                      <label htmlFor="note" className="text-900 font-semibold">
                        Poznámka
                      </label>
                      <InputTextarea
                        id="note"
                        readOnly={!isAdminOrAssistant}
                        value={values.note ?? ''}
                        onChange={e => void setFieldValue('note', e.target.value)}
                        rows={4}
                        autoResize
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button
                      type="button"
                      label="Späť"
                      icon="pi pi-arrow-left"
                      className="p-button-secondary"
                      onClick={() => navigate(`/nfa-cards?p=${tempParameters}`)}
                    />
                    {isAdminOrAssistant && (
                      <Button style={{ backgroundColor: '#e31818' }} type="submit" label="Uložiť" icon="pi pi-check" disabled={isSaving} />
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default CreateNfaCard;
